import i18n from '../i18n';

/**
 * Utility class for formatting dates in a human-readable relative format
 * Provides "x minutes ago", "x days ago", etc. formatting
 */
export class DateFormatter {
  /**
   * Formats a date string into a relative time format
   * @param dateString ISO date string or Date object
   * @returns Formatted relative time string
   */
  static formatRelativeTime(dateString: string | Date): string {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    
    // Convert to different time units
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    // Handle future dates
    if (diffInMs < 0) {
      return i18n.t('time.justNow');
    }

    // Less than 1 minute
    if (diffInMinutes < 1) {
      return i18n.t('time.justNow');
    }

    // Minutes (1-59)
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 
        ? i18n.t('time.minuteAgo') 
        : i18n.t('time.minutesAgo', { count: diffInMinutes });
    }

    // Hours (1-23)
    if (diffInHours < 24) {
      return diffInHours === 1 
        ? i18n.t('time.hourAgo') 
        : i18n.t('time.hoursAgo', { count: diffInHours });
    }

    // Days (1-6)
    if (diffInDays < 7) {
      return diffInDays === 1 
        ? i18n.t('time.dayAgo') 
        : i18n.t('time.daysAgo', { count: diffInDays });
    }

    // Weeks (1-3)
    if (diffInWeeks < 4) {
      return diffInWeeks === 1 
        ? i18n.t('time.weekAgo') 
        : i18n.t('time.weeksAgo', { count: diffInWeeks });
    }

    // Months (1-11)
    if (diffInMonths < 12) {
      return diffInMonths === 1 
        ? i18n.t('time.monthAgo') 
        : i18n.t('time.monthsAgo', { count: diffInMonths });
    }

    // Years
    return diffInYears === 1 
      ? i18n.t('time.yearAgo') 
      : i18n.t('time.yearsAgo', { count: diffInYears });
  }

  /**
   * Formats a date for tooltip display (full date and time)
   * @param dateString ISO date string or Date object
   * @returns Formatted full date string
   */
  static formatFullDate(dateString: string | Date): string {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleString();
  }
}

export default DateFormatter;